import React from 'react';
import { cn } from '@/lib/utils';

/** Static pill colours (navy text on every tint; `navy` = sheet text). */
export const PILL_TONES = {
  lilac: 'bg-brand-lilac text-navy',
  peach: 'bg-brand-peach text-navy',
  sage: 'bg-brand-sage text-navy',
  butter: 'bg-brand-butter text-navy',
  sheet: 'border border-brand-rule bg-brand-sheet text-navy',
  navy: 'bg-navy text-brand-sheet',
};

/**
 * A static, fully rounded label: a category or a truthful offer/discovery badge
 * ("Restaurant", "Offer available"). Not a control — for a filter use FilterPill.
 * Never use it for invented ratings, popularity or scarcity.
 *
 * Props:
 * - `tone`: 'lilac' (default) | 'peach' | 'sage' | 'butter' | 'sheet' | 'navy'
 * - `icon`: optional decorative icon node (14px)
 * - `as`: element (default 'span')
 * - `className`, `children`, other props pass through.
 */
export function Pill({ tone = 'lilac', icon, as: Tag = 'span', className, children, ...rest }) {
  return (
    <Tag
      className={cn(
        'inline-flex w-fit max-w-full items-center gap-[7px] rounded-full px-2.5 py-1.5 font-label text-[0.6875rem] leading-[1.4]',
        PILL_TONES[tone] || PILL_TONES.lilac,
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span aria-hidden="true" className="inline-flex shrink-0 [&_svg]:size-3.5">
          {icon}
        </span>
      ) : null}
      <span className="min-w-0">{children}</span>
    </Tag>
  );
}

/**
 * A category filter: a toggle `<button>` with `aria-pressed`, an optional icon and a
 * result count. 44px tall; the brand focus ring comes from the global rule.
 *
 * Props:
 * - `pressed`: boolean (default false) — the selected state
 * - `onPressedChange(next)`: called with the new state on click (after `onClick`,
 *   unless `onClick` called preventDefault)
 * - `onClick`: optional, receives the click event
 * - `icon`: optional decorative icon node (16px)
 * - `count`: optional number shown in a small badge (hidden from screen readers)
 * - `countLabel`: what screen readers hear for the count (default "(<count>)"), e.g.
 *   "(4 businesses)" — pass page copy here
 * - `disabled`, `className`, `children` (the label), other props pass through.
 */
export function FilterPill({
  pressed = false,
  onPressedChange,
  onClick,
  icon,
  count,
  countLabel,
  disabled = false,
  className,
  children,
  ...rest
}) {
  const handleClick = (e) => {
    if (onClick) onClick(e);
    if (!e.defaultPrevented && onPressedChange) onPressedChange(!pressed);
  };
  const hasCount = count !== undefined && count !== null;
  return (
    <button
      type="button"
      aria-pressed={pressed ? 'true' : 'false'}
      disabled={disabled}
      onClick={handleClick}
      className={cn(
        'inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap rounded-full border px-[0.8125rem] py-2 font-body text-xs leading-tight',
        'transition-[background-color,border-color,color,transform] duration-180 ease-out disabled:cursor-default disabled:opacity-50',
        pressed
          ? 'border-navy bg-navy text-brand-sheet shadow-site-pill-on'
          : 'border-brand-rule bg-brand-paper text-navy shadow-site-pill enabled:hover:border-navy/25 enabled:hover:bg-brand-lilac motion-safe:enabled:hover:-translate-y-px',
        className,
      )}
      {...rest}
    >
      {icon ? (
        <span
          aria-hidden="true"
          className={cn('inline-flex shrink-0 [&_svg]:size-4', pressed ? 'text-brand-peach' : 'text-brand-muted')}
        >
          {icon}
        </span>
      ) : null}
      <span>{children}</span>
      {hasCount ? (
        <>
          <span
            aria-hidden="true"
            className={cn(
              'min-w-[1.1875rem] rounded-full px-[5px] py-[3px] text-center font-label text-[0.625rem] leading-none',
              pressed ? 'bg-brand-sheet/15' : 'bg-navy/5',
            )}
          >
            {count}
          </span>
          <span className="sr-only">{' '}{countLabel ?? `(${count})`}</span>
        </>
      ) : null}
    </button>
  );
}

export default Pill;
